/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type FormEvent, type ReactElement, useCallback, useRef, useState, useEffect, useMemo } from 'react';
import { useNavigate } from '@/hooks/use-navigate';
import debounce from 'lodash.debounce';
import { Input } from '@/components/ui/input';
import { Popover, PopoverTrigger } from '@/components/ui/popover';
import { ArrowLeft, Search as SearchIcon, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Suggestions from '@/components/search/suggestions';
import { useSearchSuggestions } from '@/hooks/use-search-suggestions';
import { useTransformSearchSuggestions } from '@/hooks/use-transform-search-suggestions';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { getSessionJSONItem, setSessionJSONItem, clearSessionJSONItem, cn } from '@/lib/utils';
import { openShopperAgentAndSendMessage } from '@/components/shopper-agent';
import { validateShopperAgentConfig } from '@/components/shopper-agent/shopper-agent.utils';
import { UITarget } from '@/targets/ui-target';

const RECENT_SEARCH_LIMIT = 5;
const RECENT_SEARCH_KEY = 'recent-search-key';
const RECENT_SEARCH_MIN_LENGTH = 3;

interface SearchBarProps {
    autoFocus?: boolean;
    layout?: 'desktop' | 'overlay';
    placeholder?: string;
    onSearchComplete?: () => void;
}

export default function SearchBar({
    autoFocus = false,
    layout = 'desktop',
    placeholder,
    onSearchComplete,
}: SearchBarProps): ReactElement {
    const { t } = useTranslation('header');
    const isDesktop = layout === 'desktop';
    const searchPlaceholder = placeholder ?? (isDesktop ? 'Try searching “T-shirts”' : t('searchPlaceholder'));
    const navigate = useNavigate();
    const config = useConfig();
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [query, setQuery] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const queryRef = useRef(query);
    const refetchRef = useRef<() => Promise<void>>(() => Promise.resolve());

    const { data: suggestions, refetch } = useSearchSuggestions({
        q: query,
        expand: ['images', 'prices'],
        includeEinsteinSuggestedPhrases: true,
        enabled: query.trim().length >= RECENT_SEARCH_MIN_LENGTH,
    });

    const transformedSuggestions = useTransformSearchSuggestions(suggestions);

    useEffect(() => {
        queryRef.current = query;
        refetchRef.current = refetch;
    }, [query, refetch]);

    const saveRecentSearch = useCallback((searchText: string) => {
        let searches = getSessionJSONItem<string[]>(RECENT_SEARCH_KEY) || [];
        searches = searches.filter((savedSearchTerm) => {
            return searchText.toLowerCase() !== savedSearchTerm.toLowerCase();
        });
        searches = [searchText, ...searches].slice(0, RECENT_SEARCH_LIMIT);
        setSessionJSONItem(RECENT_SEARCH_KEY, searches);
    }, []);

    const debouncedRefetch = useMemo(() => {
        return debounce(() => {
            const currentQuery = queryRef.current;
            if (currentQuery.trim().length >= RECENT_SEARCH_MIN_LENGTH) {
                void refetchRef.current();
            }
        }, config.pages.search.suggestionsDebounce);
    }, [config.pages.search.suggestionsDebounce]);

    useEffect(() => {
        if (query.trim().length >= RECENT_SEARCH_MIN_LENGTH) {
            debouncedRefetch();
        } else {
            debouncedRefetch.cancel();
        }

        return () => {
            debouncedRefetch.cancel();
        };
    }, [query, debouncedRefetch]);

    const shouldOpenPopover = useCallback(() => {
        const recentSearches = getSessionJSONItem<string[]>(RECENT_SEARCH_KEY) || [];
        const searchSuggestionsAvailable =
            transformedSuggestions &&
            (transformedSuggestions.categorySuggestions.length > 0 ||
                transformedSuggestions.productSuggestions.length > 0 ||
                (transformedSuggestions.popularSearchSuggestions?.length ?? 0) > 0);

        if (
            (document.activeElement === inputRef.current && recentSearches.length > 0) ||
            (searchSuggestionsAvailable && inputRef.current?.value && inputRef.current.value.length > 0)
        ) {
            setShowSuggestions(true);
        } else {
            setShowSuggestions(false);
        }
    }, [transformedSuggestions]);

    const handleInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            setQuery(value);
            shouldOpenPopover();
        },
        [shouldOpenPopover]
    );

    const handleClearQuery = useCallback(() => {
        setQuery('');
        if (inputRef.current) {
            inputRef.current.value = '';
            inputRef.current.focus();
        }
        shouldOpenPopover();
    }, [shouldOpenPopover]);

    const handleSubmit = useCallback(
        (e: FormEvent) => {
            e.preventDefault();
            if (inputRef.current?.value?.trim()) {
                const searchQuery = inputRef.current.value.trim();
                saveRecentSearch(searchQuery);
                setShowSuggestions(false);
                void navigate(`/search?q=${encodeURIComponent(searchQuery)}`, {
                    state: { query: searchQuery },
                });
                return true;
            }
            return false;
        },
        [navigate, saveRecentSearch]
    );

    const closeAndNavigate = useCallback(
        (link: string) => {
            inputRef.current?.blur();
            setShowSuggestions(false);
            setQuery('');
            if (inputRef.current) {
                inputRef.current.value = '';
            }
            if (link) {
                void navigate(link);
            }
            onSearchComplete?.();
        },
        [navigate, onSearchComplete]
    );

    const clearRecentSearches = useCallback(() => {
        clearSessionJSONItem(RECENT_SEARCH_KEY);
        setShowSuggestions(false);
    }, []);

    const showShopperAgent =
        (config.commerceAgent?.enabled === 'true' || config.commerceAgent?.enabled === true) &&
        validateShopperAgentConfig(config.commerceAgent);

    const onShopperAgentClick = useCallback(() => {
        const searchText = inputRef.current?.value?.trim() ?? query.trim();
        setShowSuggestions(false);
        setQuery('');
        if (inputRef.current) {
            inputRef.current.value = '';
        }
        openShopperAgentAndSendMessage(searchText);
        onSearchComplete?.();
    }, [onSearchComplete, query]);

    useEffect(() => {
        shouldOpenPopover();
    }, [query, suggestions, shouldOpenPopover]);

    useEffect(() => {
        if (autoFocus) {
            inputRef.current?.focus();
        }
    }, [autoFocus]);

    const isOverlay = layout === 'overlay';

    return (
        <UITarget targetId="sfcc.header.search.input">
            <Popover open={showSuggestions}>
                <div
                    className={
                        isOverlay
                            ? 'relative z-10 w-full rounded-[5px] border-[0.5px] border-black'
                            : 'hidden md:block relative z-10 w-[363px] rounded-[8px] border-[0.5px] border-black'
                    }>
                    <div className={isOverlay ? 'flex shrink-0 items-center gap-2' : ''}>
                        {isOverlay && (
                            <button
                                type="button"
                                aria-label="Back"
                                onMouseDown={() => onSearchComplete?.()}
                                className="shrink-0 text-header-foreground">
                                <ArrowLeft className="size-6" />
                            </button>
                        )}
                        <form
                            onSubmit={(event) => {
                                if (handleSubmit(event)) {
                                    onSearchComplete?.();
                                }
                            }}
                            className={
                                isOverlay
                                    ? 'relative z-10 flex-1 rounded-[5px] border   border-[0.5px]'
                                    : 'relative z-10 w-[363px] rounded-[8px] border   border-[0.5px]'
                            }>
                            <div className="relative">
                                <PopoverTrigger asChild>
                                    <Input
                                        ref={inputRef}
                                        type="text"
                                        placeholder={searchPlaceholder}
                                        value={query}
                                        className={cn(
                                            'border-0 w-full pl-10 pr-10 focus-visible:border-header-foreground focus-visible:ring-1 focus-visible:ring-header-foreground',
                                            isDesktop &&
                                                'font-lato font-normal text-sm leading-[21px] tracking-normal text-foreground placeholder:font-lato placeholder:font-normal placeholder:text-sm placeholder:leading-[21px] placeholder:tracking-normal placeholder:text-foreground placeholder:opacity-100'
                                        )}
                                        onChange={handleInputChange}
                                        onFocus={shouldOpenPopover}
                                        onBlur={() => setShowSuggestions(false)}
                                        aria-label={t('searchPlaceholder')}
                                        aria-autocomplete="list"
                                        aria-expanded={showSuggestions}
                                        aria-haspopup="listbox"
                                        role="combobox"
                                        data-testid="header-search"
                                    />
                                </PopoverTrigger>
                                <SearchIcon
                                    className={cn(
                                        'pointer-events-none absolute left-3 top-1/2 -translate-y-1/2',
                                        isDesktop ? 'size-4' : 'size-5'
                                    )}
                                />
                                {query.length > 0 && (
                                    <button
                                        type="button"
                                        aria-label="Clear search"
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            handleClearQuery();
                                        }}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-header-foreground">
                                        <X className="size-5" />
                                    </button>
                                )}
                            </div>
                        </form>
                    </div>

                    {showSuggestions && !isOverlay && (
                        <div
                            className="absolute left-0 top-full z-50 mt-1 max-h-[462px] w-[363px] overflow-y-auto rounded-lg border-[0.5px] border-black bg-white px-3 py-5 shadow-[0px_1px_12px_rgba(0,0,0,0.25)]"
                            role="listbox"
                            aria-label={t('searchSuggestions')}>
                            <Suggestions
                                searchSuggestions={transformedSuggestions}
                                recentSearches={getSessionJSONItem<string[]>(RECENT_SEARCH_KEY) || []}
                                closeAndNavigate={closeAndNavigate}
                                clearRecentSearches={clearRecentSearches}
                                showShopperAgent={showShopperAgent}
                                onShopperAgentClick={onShopperAgentClick}
                                compact
                            />
                        </div>
                    )}

                    {showSuggestions && isOverlay && (
                        <div
                            className="mt-2 min-h-0 flex-1 w-full overflow-y-auto rounded-[4px] px-3 py-5"
                            role="listbox"
                            aria-label={t('searchSuggestions')}>
                            <Suggestions
                                searchSuggestions={transformedSuggestions}
                                recentSearches={getSessionJSONItem<string[]>(RECENT_SEARCH_KEY) || []}
                                closeAndNavigate={closeAndNavigate}
                                clearRecentSearches={clearRecentSearches}
                                showShopperAgent={showShopperAgent}
                                onShopperAgentClick={onShopperAgentClick}
                                compact
                            />
                        </div>
                    )}
                </div>
            </Popover>
        </UITarget>
    );
}
